/**
 * The public site per language, in the running page (step 73.11).
 *
 * The Worker writes every language's address into the served HTML of a public
 * page (`worker/languageAlternates.ts`) — that is what a crawler reads. This
 * keeps the same `<link rel="alternate" hreflang>` lines true as the app moves
 * between public pages without a reload (the public site → an artist page),
 * and carries `?lang=` on the public pages' links while it is in the address,
 * so a page found in Hebrew stays Hebrew one click later. Neither is ever a
 * saved choice (`LocaleProvider`). Renders nothing.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LANG_PARAM, languageAlternates } from '@myrave/shared';
import { isPublicPageHost, platformDomain } from '@/lib/tenant';
import { useLocale } from './LocaleProvider';

/** The head's alternates for this address — replaced, never added twice. */
export function syncAlternateLinks(href: string, doc: Document = document): void {
  for (const old of Array.from(doc.head.querySelectorAll('link[rel="alternate"][hreflang]'))) old.remove();
  for (const alt of languageAlternates(href)) {
    const link = doc.createElement('link');
    link.rel = 'alternate';
    link.hreflang = alt.hreflang;
    link.href = alt.href;
    doc.head.appendChild(link);
  }
}

/**
 * The address a link should go to while this page's address names a locale:
 * the same, with `lang`, when it leads to a public page or the sign-in —
 * never to another site, and never over a `lang` the link already has.
 */
export function withAddressLocale(href: string, locale: string, base: string, platform = platformDomain()): string | null {
  let url: URL;
  try { url = new URL(href, base); } catch { return null; }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.toLowerCase();
  const login = host === `login.${platform.toLowerCase()}`;
  if (!login && !isPublicPageHost(host, platform)) return null;
  if (url.searchParams.has(LANG_PARAM)) return null;
  // A jump within this page (`#download`) is not a new address.
  const here = new URL(base);
  if (url.hash && url.origin === here.origin && url.pathname === here.pathname && url.search === here.search) return null;
  url.searchParams.set(LANG_PARAM, locale);
  return url.toString();
}

export function LanguageAlternates() {
  const { pathname } = useLocation();
  const { addressLocale } = useLocale();
  const isPublic = isPublicPageHost(window.location.hostname);

  useEffect(() => {
    if (isPublic) syncAlternateLinks(window.location.href);
  }, [isPublic, pathname]);

  useEffect(() => {
    if (!addressLocale) return;
    // At the moment of the click, so every link on every public page carries
    // it — the ones a screen draws later too — with nothing to remember in
    // each component. The browser follows the `href` it finds after this.
    const carry = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const next = withAddressLocale(a.getAttribute('href') ?? '', addressLocale, window.location.href);
      if (next) a.setAttribute('href', next);
    };
    document.addEventListener('click', carry, true);
    document.addEventListener('auxclick', carry, true);
    return () => {
      document.removeEventListener('click', carry, true);
      document.removeEventListener('auxclick', carry, true);
    };
  }, [addressLocale]);

  return null;
}
